import {
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { NewPayment } from '../components/PaymentModal'
import { EmployeeInfo } from '../pages/Employee/Home'
import { validateMasterPassword } from '../services/configs'
import {
    CashierClosedSaleError,
    CreatePreSaleInterface,
    ParaguayTransfer,
    Pix,
    createPreSale,
    createSale,
    getPreSaleById,
    getSaleById,
    updatePreSale,
    updateSale,
} from '../services/sale'
import {
    getExchangeValue,
    getIvaEffectiveRate,
    getProductStockInBranch,
    getTotalIvaCalculated,
    maskCurrency,
} from '../utils/maks'
import { AccountInfo, useAccount } from './account_context'
import { BranchInfo } from './branch_context'
import { BuyInfo, PaymentType } from './buy_context'
import { CashierInfo } from './cashier_context'
import {
    ClientDocumentType,
    ClientInfo,
    anonymousClient,
    useClient,
} from './client_context'
import { CoinInfo, ExchageInfo, useCoin } from './coin_context'
import { CalculateGain, SystemBranchEnum, useConfigs } from './configs_context'
import { DiscountInfo, useDiscount } from './discount_context'
import { useLoading } from './loading_context'
import { NoteInfo } from './notes_context'
import { usePassword } from './password_context'
import { PaymentMethodInfo } from './payment_method_context'
import {
    IvaEnum,
    NotFoundProductFromExcel,
    ProductInLoadLote,
    ProductInLoadSerial,
    ProductInfo,
} from './product_context'
import { useSnack } from './snack_context'
import { SupplierInfo } from './supplier_context'
import { config } from 'process'
import { prod } from '@tensorflow/tfjs-core'
import { loadProductsFromExcel } from '../services/product'
import GenericModal from '../components/GenericModal'
import { List, ListItem, ListItemText, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
const currency = require('currency.js')

export interface ProductInSale {
    id: number
    product: ProductInfo
    price: number
    notePrice: number
    qnt: number
    subtotal: number
    discount: number
    systemDiscount: Partial<DiscountInfo> | null
    buyPrice?: number
    baseBuyPrice?: number
    ivaValue?: number
    proporcaoIva?: number
    selectedNamedPrice?: string
    unitPrice?: number
    date?: string
    hour?: string
    isSummerTime?: boolean
    prevSaleId?: number
    prevSale?: SaleInfo
    preSaleProductId?: number
    returnedQnt?: number
    deliveredQnt?: number
    valueSecondaryCoin?: {
        iso: string
        coinId: number
        price: number
        notePrice: number
        buyPrice: number
        baseBuyPrice: number
        unitPrice: number
    }[]
    saleId?: number
    productsInDelivery?: ProductInDelivery[]
    noteProduct?: ProductInfo | null
    tmpPrice?: number
}

export interface PaymentChange {
    coin: CoinInfo
    value: number
}

export interface PaymentInSale {
    id?: number
    paymentCoin: CoinInfo | null
    amountPayed: number
    discount: number
    creditDiscount: number
    paymentMethod: PaymentMethodInfo | null
    madeBy: AccountInfo | null
    date?: string
    hour?: string
    cashier?: CashierInfo
    exchange?: {
        exchangeValue: number
        exchangeAmountPayed: number
        flagReverseExchange: number
    }
    proofText?: string
    proofImage?: string
    isSummerTime?: boolean
    bill?: string
    note?: NoteInfo | null
    noteDate?: string
    noteHour?: string
    isNoteSummerTime?: boolean
    paymentStatus?: number
    employee?: EmployeeInfo
    sale?: SaleInfo
    isJoinedPayment?: boolean
    prevPayments?: PaymentInSale[]
    feesPayed: number
    changes: PaymentChange[]
}

export enum SaleStatus {
    Pending = 0,
    Open = 1,
    Closed = 2,
    Canceled = 3,
    Expired = 4,
}

export enum SalePaymentStatus {
    Pending = 0,
    Created = 1,
    Canceled = 2,
}

export const SaleStatusLabel: Record<number, string> = {
    0: 'Pendiente',
    1: 'Abierta',
    2: 'Cerrada',
    3: 'Cancelada',
    4: 'Expirada',
}

export const SalePaymentStatusLabel: Record<number, string> = {
    0: 'Pendiente',
    1: 'Pago',
    2: 'Cancelado',
}

export enum DeliveryStatus {
    NotDelivered = 0,
    Delivered = 1,
}

export const DeliveryStatusLabel: Record<number, string> = {
    0: 'Não entregue',
    1: 'Entregue',
}

export interface SaleInfo {
    id?: number
    bill: string | null
    products: ProductInSale[]
    selectedProducts?: ProductInSale[]
    prevEditingProducts?: ProductInSale[]
    client: ClientInfo | null
    totalIva: number
    total: number
    totalAmoutPayed: number
    totalFees: number
    payments: PaymentInSale[]
    observation: string
    paymentType: number | null
    paymentTypeShow: number | null
    saleStatus: number
    initialFees: number
    note: NoteInfo | null
    branch: BranchInfo | null
    date?: string
    hour?: string
    expDate?: string
    expHour?: string
    noteDate?: string
    noteHour?: string
    isNoteSummerTime?: boolean
    coin?: CoinInfo
    cashier?: CashierInfo
    account?: AccountInfo
    productCount?: number
    isSummerTime?: boolean
    preSale?: PreSaleInfo
    totalIvaFivePercent?: number
    totalIvaTenPercent?: number
    totalExcept?: number
    totalFivePercent?: number
    totalTenPercent?: number
    cdc?: string
    qrCode?: string
    employee?: EmployeeInfo
    guide?: EmployeeInfo
    statusEletronicNote?: number
    logEletronicNote?: string
    isEditing?: boolean
    creditNotes?: CreditNoteInfo[]
    isJoinedNote?: boolean
    branchId?: number
    totalSecondaryCoin?: {
        exchange: number
        iso: string
        coinId: number
        totalIvaFivePercent: number
        totalIvaTenPercent: number
        totalIva: number
        totalExcept: number
        totalFivePercent: number
        totalTenPercent: number
        total: number
    }[]
    guidePercentage?: number
    totalParcels?: number
    deliveryStatus?: number
    preSaleId?: number
    comission?: number
}

export interface ProductInPreSale {
    id: number
    product: ProductInfo
    price: number
    notePrice: number
    qnt: number
    subtotal: number
    discount: number
    selectedNamedPrice?: string
    ivaValue?: number
    proporcaoIva?: number
    tmpPrice?: number
    soldQnt?: number
}

export interface PreSaleInfo {
    id?: number
    title: string
    products: ProductInPreSale[]
    branch: BranchInfo | null
    totalIva: number
    total: number
    annotation: string | null
    client: ClientInfo | null
    date?: string
    hour?: string
    cashier?: CashierInfo
    account?: AccountInfo
    productCount?: number
    isSummerTime?: boolean
    isActive?: boolean
    isEditing?: boolean
    employee: EmployeeInfo | null
    coin?: CoinInfo
    reservedStock: boolean
}

export interface SaleReportsInfo {
    coinId: number
    totalSold: number
    totalFees: number
    totalPaid: number
    totalCost: number
    totalGain: number
    totalInCreditNotes: number
    totalDebt: number
    totalSales: number
    totalDiscount: number
    totalGainBase: number
    totalCostBase: number
    totalIva: number
    coin?: CoinInfo
}

export interface SoldProductsInfo {
    productId: number
    product: string
    averageBuyPrice: string
    averageBuyPriceBase: string
    averageSalePrice: string
    averageGain: string
    averageGainBase: string
    averageDiscount: string
    qntSold: string
    qntSales: string
    clientName?: string
    coin?: CoinInfo
}

export interface ParcelInfo {
    id: number
    total: number
    totalPaid: number
    totalFees: number
    endDate: string
    endHour: string
    status: number
    saleId: number
    payments: PaymentInSale[]
    parcelNumber: number
    numeroFaturaLegal?: string | null
    cdc?: string | null
    qrCode?: string | null
    statusEletronicNote?: number
    logEletronicNote?: string | null
    noteTimbrado?: number | null
    notePrefix?: string | null
    noteIniD?: string | null
    noteEndD?: string | null
    notaId?: number | null
    noteCoinId?: number | null
    transactionType?: number
}

export interface ProductInCreditNote {
    id?: number
    qnt: number
    price: number
    unitPrice: number
    ivaValue: number
    proporcaoIva?: number
    subtotal: number
    product: ProductInfo
}

export interface CreditNoteInfo {
    id?: number
    bill: string | null
    products: ProductInCreditNote[]
    totalIva: number
    total: number
    note: NoteInfo | null
    date?: string
    hour?: string
    isSummerTime: boolean
    type: number
    status: number
    cashier?: Partial<CashierInfo>
    account?: Partial<AccountInfo>
    totalIvaFivePercent?: number
    totalIvaTenPercent?: number
    totalExcept?: number
    totalFivePercent?: number
    totalTenPercent?: number
    cdc?: string
    qrCode?: string
    statusEletronicNote?: number
    logEletronicNote?: string
    noteDate?: string
    noteHour?: string
    isNoteSummerTime?: boolean
    operations?: {
        sale?: SaleInfo
        buy?: BuyInfo
    }[]
    coin?: CoinInfo
    branch?: BranchInfo
    client?: ClientInfo
    supplier?: SupplierInfo
    isDebitNote: boolean
    changedStock: boolean
    discount: number
    refundedWithExtraCredit: boolean
}

export interface SaleContract {
    id: number
    value: number
    date: string
    hour: string
    isSummerTime: boolean
    expDate: string
    expHour: string
    sale?: SaleInfo | null
    parcel?: ParcelInfo | null
    client?: ClientInfo | null
    coin?: CoinInfo | null
    branch?: BranchInfo | null
}

export interface ProductInDelivery {
    id: number
    qnt: number
    product?: ProductInfo
    productSerial?: ProductInLoadSerial
    serial?: string | null
    productLote?: ProductInLoadLote
    lote?: string | null
    refundedQnt: number
    productSale?: ProductInSale
}

export interface DeliveryInfo {
    id: number
    cashier?: Partial<CashierInfo>
    account?: Partial<AccountInfo>
    date?: string
    hour?: string
    isSummerTime: boolean
    sale?: SaleInfo
    products: ProductInDelivery[]
}

export enum CreditNoteStatus {
    Created = 0,
    Canceled = 1,
}

export const CreditNoteStatusLabel: Record<number, string> = {
    0: 'Creado',
    1: 'Cancelado',
}

export enum CreditNoteType {
    SpecificSale = 0,
    GeneralSale = 1,
    SpecificPurchase = 2,
    GeneralPurchase = 3,
}

export const CreditNoteTypeLabel: Record<number, string> = {
    0: 'Venta específica',
    1: 'Venta general',
    2: 'Compra específica',
    3: 'Compra general',
}

export enum EletronicNoteStatus {
    NoEletronicNote = 0,
    PendingConfirmation = 1,
    Confirmed = 2,
    Error = 3,
}

export const EletronicNoteStatusLabel: Record<number, string> = {
    0: 'Sin',
    1: 'Confirmación pendiente',
    2: 'Envio confirmado',
    3: 'Error',
}

export enum ParcelStatus {
    Pending = 0,
    Paid = 1,
    Expired = 2,
    Canceled = 3,
}

export const ParcelStatusLabel: Record<number, string> = {
    0: 'Pendiente',
    1: 'Pago',
    2: 'Expirada',
    3: 'Cancelada',
}

const saleContextDefault: SaleInfo = {
    bill: '',
    products: [],
    client: anonymousClient,
    totalIva: 0,
    total: 0,
    totalAmoutPayed: 0,
    totalFees: 0,
    initialFees: 0,
    observation: '',
    paymentType: null,
    paymentTypeShow: null,
    saleStatus: SaleStatus.Open,
    payments: [],
    note: null,
    branch: null,
    isEditing: false,
    prevEditingProducts: undefined,
}

const preSaleContextDefault: PreSaleInfo = {
    title: '',
    products: [],
    branch: null,
    total: 0,
    totalIva: 0,
    annotation: null,
    client: anonymousClient,
    employee: null,
    reservedStock: false,
}

export const getClientLabel = (client: ClientInfo) => {
    if (client.id === 0) return client.name
    return `${client.name}, número: ${client.id}, email: ${client.emails[0]}, ${
        client?.documentType === ClientDocumentType.RUC ? 'ruc' : 'documento'
    }: ${client.document}, crédito: ${client.allowCredit ? 'sim' : 'não'}`
}

export const ElectronicTransactionTypeLabel: Record<number, string> = {
    1: 'Venta de mercadería',
    2: 'Prestación de servicios',
    3: 'Mixto',
    4: 'Venta de activo fijo',
    5: 'Venta de divisas',
    6: 'Compra de divisas',
    7: 'Promoción o entrega de muestras',
    8: 'Donación',
    9: 'Anticipo',
    10: 'Compra de productos',
    11: 'Compra de servicios',
    12: 'Venta de crédito fiscal',
}

interface HandleCreateNewSaleInterface {
    paymentType?: number
    payments?: NewPayment[]
    useLegalNote?: boolean
    initialFees?: number
    parcels?: number[]
    discount?: number
    creditDiscount?: number
    changes?: PaymentChange[]
    employeeId?: number
    note?: NoteInfo | null
    noteCoin?: CoinInfo
    transactionType?: number
    isPix?: boolean
    isParaguayTransfer?: boolean
}

export interface HandleCreateNewSaleResponse {
    sale: SaleInfo | null
    pix: Pix | null
    paraguayTransfer: ParaguayTransfer | null
}

export interface SaleContextInterface {
    saleInfo: SaleInfo
    setSaleInfo: Dispatch<SetStateAction<SaleInfo>>
    preSaleInfo: PreSaleInfo
    setPreSaleInfo: Dispatch<SetStateAction<PreSaleInfo>>
    handleChangeBill(bill: string): void
    handleAddNewProducts(products: ProductInfo[]): ProductInSale[]
    handleLoadPreSale(preSale: PreSaleInfo): void
    handleRemoveProduct(product: ProductInSale, pass?: string): void
    handleChangeProductQnt(product: ProductInSale, qnt: number): void
    handleChangeProductPrice(product: ProductInSale, price: number): void
    handleChangeProductTmpPrice(product: ProductInSale, price: number): void
    handleChangeProductDiscount(
        products: ProductInSale[],
        discount: number
    ): void
    handleChangeProductSelectedNamedPrice(
        product: ProductInSale,
        selectedNamedPrice: string,
        price: number
    ): void
    handleChangeClient(client: ClientInfo | null): void
    handleChangeCoin(coin: CoinInfo): void
    handleChangePayment(paymentType: number, payment: PaymentInSale): void
    handleChangeObservation(o: string): void
    handleAddNewProductsInPreSale(products: ProductInfo[]): void
    handleRemoveProductInPreSale(product: ProductInPreSale): void
    handleChangeProductQntInPreSale(
        product: ProductInPreSale,
        qnt: number
    ): void
    handleChangeProductPriceInPreSale(
        product: ProductInPreSale,
        price: number
    ): void
    handleChangeProductTmpPriceInPreSale(
        product: ProductInPreSale,
        price: number
    ): void
    handleChangeProductDiscountInPreSale(
        product: ProductInPreSale,
        discount: number
    ): void
    handleChangeProductSelectedNamedPriceInPreSale(
        product: ProductInPreSale,
        selectedNamedPrice: string,
        price: number
    ): void
    handleChangeClientInPreSale(client: ClientInfo | null): void
    handleChangeCoinInPreSale(coin: CoinInfo): void
    handleLoadExcelInPreSale(base64: string): void
    validate(): boolean
    validatePreSale(): boolean
    handleCreateNewSale(
        data: HandleCreateNewSaleInterface
    ): Promise<HandleCreateNewSaleResponse>
    handleCreateNewPreSale(): Promise<void>
    handleChangeSelectedProducts(products: ProductInSale[]): void
    startNewPreSale(): void
    startNewSale(): void
    duplicateSale(sale: SaleInfo): void
    editSale(id: number): Promise<void>
    editPreSale(id: number): Promise<void>
    paymentMethod: number | undefined
    setPaymentMethod: Dispatch<SetStateAction<number | undefined>>
}

const saleContext = createContext<SaleContextInterface>({
    saleInfo: saleContextDefault,
    setSaleInfo: () => {},
    preSaleInfo: preSaleContextDefault,
    setPreSaleInfo: () => {},
    handleChangeBill: () => {},
    handleAddNewProducts: () => [],
    handleLoadPreSale: () => {},
    handleRemoveProduct: () => {},
    handleChangeProductQnt: () => {},
    handleChangeProductPrice: () => {},
    handleChangeProductTmpPrice: () => {},
    handleChangeProductDiscount: () => {},
    handleChangeProductSelectedNamedPrice: () => {},
    handleChangeClient: () => {},
    handleChangeCoin: () => {},
    handleChangePayment: () => {},
    handleChangeObservation: () => {},
    handleAddNewProductsInPreSale: () => {},
    handleRemoveProductInPreSale: () => {},
    handleChangeProductQntInPreSale: () => {},
    handleChangeProductPriceInPreSale: () => {},
    handleChangeProductTmpPriceInPreSale: () => {},
    handleChangeProductDiscountInPreSale: () => {},
    handleChangeProductSelectedNamedPriceInPreSale: () => {},
    handleChangeClientInPreSale: () => {},
    handleChangeCoinInPreSale: () => {},
    handleLoadExcelInPreSale: () => {},
    validate: () => false,
    validatePreSale: () => false,
    handleCreateNewSale: async () => ({
        sale: null,
        pix: null,
        paraguayTransfer: null,
    }),
    handleCreateNewPreSale: () => Promise.resolve(),
    handleChangeSelectedProducts: () => {},
    startNewSale: () => {},
    startNewPreSale: () => {},
    duplicateSale: () => {},
    editSale: () => Promise.resolve(),
    editPreSale: () => Promise.resolve(),
    paymentMethod: undefined,
    setPaymentMethod: () => {},
})

export function useSale() {
    return useContext(saleContext)
}

interface Props {
    children: ReactNode
}

export const SaleContextProvider = ({ children }: Props) => {
    const loading = useLoading()
    const snack = useSnack()
    const password = usePassword()
    const navigate = useNavigate()
    const [saleInfo, setSaleInfo] = useState<SaleInfo>(saleContextDefault)
    const [preSaleInfo, setPreSaleInfo] = useState<PreSaleInfo>(
        preSaleContextDefault
    )
    const { discounts } = useDiscount()
    const { coins } = useCoin()
    const { accountInfo } = useAccount()
    const { configs } = useConfigs()
    const [paymentMethod, setPaymentMethod] = useState<number>()
    const [notFoundProductsInExcel, setNotFoundProductsInExcel] = useState<
        NotFoundProductFromExcel[] | null
    >(null)
    const {
        setOpenRegisterClientModal,
        setClientInfo,
        setClientError,
        validate: validateClient,
    } = useClient()
    const { t } = useTranslation()

    const getProductPrice = useCallback(
        (
            product: ProductInfo,
            coin: CoinInfo,
            qnt: number,
            discount: number,
            client: ClientInfo | null,
            price?: number,
            selectedNamedPrice?: string
        ): {
            value: number
            notePrice: number
            buyPrice: number
            unitPrice: number
            iva: number
            proporcaoIva: number
            systemDiscount: DiscountInfo | null
            selectedNamedPrice: string | null
        } => {
            let iva = product.iva
            const proporcaoIva = product.proporcaoIva ?? 100

            if (
                configs.incrementIva &&
                !product.obrigatoryIva &&
                client &&
                (client.nacionality !== 'PRY' ||
                    (client.taxFreeRegimen &&
                        (!client.taxFreeRegimenLimitDate ||
                            client.taxFreeRegimenLimitDate.valueOf() >=
                                Date.now())))
            ) {
                iva = 0
            }

            let exchange: ExchageInfo | null = null

            const useAvgPrice =
                product.avgBuyPrice &&
                configs.gainCalculationType === CalculateGain.AveragePrice

            let buyPrice = useAvgPrice ? product.avgBuyPrice : product.buyPrice
            let cifPrice = useAvgPrice ? product.avgCifPrice : product.cifPrice
            let baseBuyPrice = product.buyPrice
            let unitPrice = product.salePrice

            if (product.coin?.id !== coin.id) {
                exchange =
                    coin.exchanges.find(
                        (e) => e.coin.id === product.coin?.id
                    ) ?? null
                buyPrice = getExchangeValue({
                    amount: buyPrice,
                    exchange: exchange?.saleExchange ?? 0,
                    coinExchangeType: exchange?.flagReverseExchange ?? 0,
                    order: 'reverse',
                })
                cifPrice = getExchangeValue({
                    amount: cifPrice,
                    exchange: exchange?.saleExchange ?? 0,
                    coinExchangeType: exchange?.flagReverseExchange ?? 0,
                    order: 'reverse',
                })
                baseBuyPrice = getExchangeValue({
                    amount: baseBuyPrice,
                    exchange: exchange?.saleExchange ?? 0,
                    coinExchangeType: exchange?.flagReverseExchange ?? 0,
                    order: 'reverse',
                })
                unitPrice = getExchangeValue({
                    amount: unitPrice,
                    exchange: exchange?.saleExchange ?? 0,
                    coinExchangeType: exchange?.flagReverseExchange ?? 0,
                    order: 'reverse',
                })
            }

            if (typeof price === 'number') {
                return {
                    systemDiscount: null,
                    value: price,
                    notePrice:
                        iva && configs.incrementIva
                            ? currency(price).add(
                                  currency(price)
                                      .multiply(
                                          getIvaEffectiveRate(iva, proporcaoIva)
                                      )
                                      .divide(100)
                              ).value
                            : price,
                    buyPrice: baseBuyPrice,
                    iva: iva,
                    proporcaoIva: proporcaoIva,
                    unitPrice: unitPrice,
                    selectedNamedPrice: null,
                }
            }

            let currentPrice = product.salePrice

            const foundNamedPrice = product.namedPrice
                .filter(
                    (price) =>
                        price.clients.find((c) => c.id === client?.id) ||
                        price.name === client?.prefferedPrice?.toString() ||
                        price.name === selectedNamedPrice
                )
                .sort((a, b) => a.price - b.price)

            if (foundNamedPrice.length) currentPrice = foundNamedPrice[0].price
            else {
                const foundQntPrices = product.qntPrice
                    .filter((price) => qnt >= price.qnt)
                    .sort((a, b) => b.qnt - a.qnt)
                if (foundQntPrices.length)
                    currentPrice = foundQntPrices[0].price
            }

            let foundSystemDiscountProduct = 0
            let foundSystemDiscount: DiscountInfo | null = null

            discounts
                .filter((d) => d.isActive && d.activeDate)
                .sort((a, b) => Number(b.id) - Number(a.id))
                .forEach((discount) => {
                    if (foundSystemDiscountProduct) return

                    const filteredDiscountProducts = discount.discountProducts
                        .filter((dp) =>
                            dp.products.find((p) => p.id === product.id)
                        )
                        .sort((a, b) => b.discount - a.discount)

                    if (filteredDiscountProducts.length) {
                        foundSystemDiscount = discount
                        foundSystemDiscountProduct =
                            filteredDiscountProducts[0].discount
                        return
                    }
                })

            if (foundSystemDiscountProduct)
                currentPrice = currency(currentPrice).subtract(
                    (currentPrice * foundSystemDiscountProduct) / 100
                ).value
            currentPrice = currency(currentPrice).subtract(
                (currentPrice * discount) / 100
            ).value

            if (exchange) {
                currentPrice = getExchangeValue({
                    amount: currentPrice,
                    exchange: exchange?.saleExchange ?? 0,
                    coinExchangeType: exchange?.flagReverseExchange ?? 0,
                    order: 'reverse',
                })
            }

            return {
                value: currentPrice,
                notePrice:
                    iva && configs.incrementIva
                        ? currency(currentPrice).add(
                              currency(currentPrice)
                                  .multiply(
                                      getIvaEffectiveRate(iva, proporcaoIva)
                                  )
                                  .divide(100)
                          ).value
                        : currentPrice,
                buyPrice: baseBuyPrice,
                systemDiscount: foundSystemDiscount,
                iva: iva,
                proporcaoIva: proporcaoIva,
                unitPrice: unitPrice,
                selectedNamedPrice: foundNamedPrice[0]?.name ?? null,
            }
        },
        [configs, discounts]
    )

    const getTotal = useCallback((products: ProductInPreSale[]): number => {
        const total = products
            .map((p) => p.subtotal)
            .reduce((total, current) => {
                return currency(total).add(current).value
            }, 0)
        return total
    }, [])

    const getTotalIva = useCallback((products: ProductInPreSale[]): number => {
        return getTotalIvaCalculated(
            products.map((p) => ({
                iva: p.ivaValue,
                proporcao: p.proporcaoIva,
                preco: p.notePrice,
                quantidade: p.qnt,
            }))
        )
    }, [])

    const handleChangeBill = useCallback((bill: string) => {
        setSaleInfo((prev) => ({
            ...prev,
            bill: bill,
        }))
    }, [])

    const handleAddNewProducts = useCallback(
        (products: ProductInfo[]) => {
            const isMasterBranch =
                !accountInfo?.branch &&
                configs.systemBranch === SystemBranchEnum.Branch
            if (isMasterBranch && !saleInfo.branch) {
                snack.error('Seleccione una sucursal!')
                throw new Error('Seleccione una sucursal!')
            }

            const newProducts = [...saleInfo.products]
            let count = saleInfo.productCount ?? 0

            const productsToReturn: ProductInSale[] = []

            products.forEach((product) => {
                const weight = product.weight
                    ? product.weight
                    : product.unitType === 'Un'
                      ? 1
                      : 0.01

                let stock = getProductStockInBranch(
                    product,
                    saleInfo.branch,
                    isMasterBranch
                )

                if (saleInfo.prevEditingProducts && saleInfo.isEditing) {
                    const foundPrevProduct = saleInfo.prevEditingProducts.find(
                        (p) => p.product.id === product.id
                    )

                    if (foundPrevProduct) {
                        stock = currency(stock).add(foundPrevProduct.qnt).value
                    }
                }

                const productInSale = saleInfo.products.find(
                    (p) => p.product.id === product.id
                )
                if (productInSale && productInSale.product.autoSum) {
                    handleChangeProductQnt(
                        productInSale,
                        currency(productInSale.qnt).add(weight).value
                    )
                    //productsToReturn.push(productInSale)
                    return
                }
                if (newProducts.length === configs.productLimitInSale) {
                    snack.error(
                        `¡Se ha excedido el límite de productos para la venta! (${
                            configs.productLimitInSale
                        } iten${configs.productLimitInSale !== 1 ? 's' : ''}.)`
                    )
                    return
                }
                if (!product.isAtive) {
                    snack.error('O produto está desativado!')
                    return
                }
                if (!product.sellWithoutStock && stock < weight) {
                    snack.error('O produto está sem estoque!')
                    return
                }

                let price = product.salePrice

                if (
                    product.coin?.id &&
                    product.coin?.id !== saleInfo.coin?.id
                ) {
                    const exchange =
                        saleInfo.coin?.exchanges.find(
                            (e) => e.coin.id === product.coin?.id
                        ) ?? null
                    price = getExchangeValue({
                        amount: price,
                        exchange: exchange?.saleExchange ?? 0,
                        coinExchangeType: exchange?.flagReverseExchange ?? 0,
                        order: 'reverse',
                    })
                }

                const {
                    value,
                    systemDiscount,
                    notePrice,
                    iva,
                    proporcaoIva,
                    buyPrice,
                    unitPrice,
                    selectedNamedPrice,
                } = getProductPrice(
                    product,
                    saleInfo.coin!,
                    weight,
                    0,
                    saleInfo.client
                    /* product.autoPrice ? price : undefined */
                )

                count += 1

                const newProduct: ProductInSale = {
                    product: product,
                    price: value,
                    notePrice: notePrice,
                    qnt: weight,
                    subtotal: notePrice * weight,
                    id: count,
                    discount: 0,
                    systemDiscount: systemDiscount,
                    ivaValue: iva,
                    proporcaoIva: proporcaoIva,
                    buyPrice: buyPrice,
                    unitPrice: unitPrice,
                    selectedNamedPrice: selectedNamedPrice ?? undefined,
                }

                newProducts.push(newProduct)
                productsToReturn.push(newProduct)
            })

            setSaleInfo((prev) => ({
                ...prev,
                products: newProducts,
                total: getTotal(newProducts),
                totalIva: getTotalIva(newProducts),
                productCount: count,
            }))

            return productsToReturn
        },
        [
            accountInfo,
            configs,
            saleInfo,
            snack,
            getProductPrice,
            getTotal,
            getTotalIva,
        ]
    )

    const handleLoadPreSale = useCallback(
        async (preSale: PreSaleInfo) => {
            const isMasterBranch =
                !accountInfo?.branch &&
                configs.systemBranch === SystemBranchEnum.Branch
            if (isMasterBranch && !saleInfo.branch) {
                snack.error('Selecione una sucursal!')
                return
            }
            if (isMasterBranch && saleInfo.branch?.id !== preSale.branch?.id) {
                snack.error('A pré-venda não pertence a filial!')
                return
            }

            preSale = await getPreSaleById(preSale.id as number, coins)

            const newProducts: ProductInSale[] = []
            let count = saleInfo.productCount ?? 0

            preSale.products.forEach((product) => {
                const stock = getProductStockInBranch(
                    product.product,
                    saleInfo.branch,
                    isMasterBranch
                )

                const finalQnt = currency(product.qnt).subtract(
                    product.soldQnt
                ).value

                if (finalQnt <= 0) return

                if (!product.product.isAtive) {
                    snack.error('O produto está desativado!')
                    return
                }
                if (!product.product.sellWithoutStock && stock < finalQnt) {
                    snack.error('O produto está sem estoque!')
                    return
                }

                const {
                    value,
                    systemDiscount,
                    notePrice,
                    iva,
                    proporcaoIva,
                    buyPrice,
                    unitPrice,
                } = getProductPrice(
                    product.product,
                    saleInfo.coin!,
                    finalQnt,
                    product.discount,
                    preSale.client,
                    product.product.autoPrice ? product.price : undefined,
                    product.selectedNamedPrice
                )

                count += 1
                newProducts.push({
                    product: product.product,
                    price: value,
                    notePrice: notePrice,
                    qnt: finalQnt,
                    subtotal: notePrice * finalQnt,
                    id: count,
                    discount: product.discount,
                    systemDiscount: systemDiscount,
                    selectedNamedPrice: product.selectedNamedPrice,
                    ivaValue: iva,
                    proporcaoIva: proporcaoIva,
                    buyPrice: buyPrice,
                    unitPrice: unitPrice,
                    preSaleProductId: configs.controlProductsInPreSale
                        ? product.id
                        : undefined,
                })
            })

            setSaleInfo((prev) => ({
                ...prev,
                products: newProducts,
                total: getTotal(newProducts),
                totalIva: getTotalIva(newProducts),
                productCount: count,
                preSale: preSale,
                client: preSale.client,
                observation: preSale.annotation ?? '',
                employee: preSale.employee ?? undefined,
                coin: preSale.coin,
            }))
        },
        [
            accountInfo,
            configs,
            saleInfo,
            snack,
            coins,
            getProductPrice,
            getTotal,
            getTotalIva,
        ]
    )

    const handleRemoveProduct = useCallback(
        async (product: ProductInSale, pass?: string) => {
            if (configs.useMasterPassword) {
                if (!pass) {
                    password.show({
                        confirmPassword: false,
                        returnCb: (pass) => handleRemoveProduct(product, pass),
                        whatsappContext: {
                            action: 'remove_product',
                            user: accountInfo?.name,
                            product: product.product?.name,
                        },
                    })
                    return
                } else {
                    loading.show()
                    try {
                        await validateMasterPassword(pass)
                        password.hide()
                        loading.hide()
                    } catch (error) {
                        password.showError('Senha inválida!')
                        loading.hide()
                        return
                    }
                }
            }

            const newProducts = [...saleInfo.products]
            const index = newProducts.findIndex((p) => p.id === product.id)
            if (index === -1) return

            newProducts.splice(index, 1)

            setSaleInfo((prev) => ({
                ...prev,
                products: newProducts,
                total: getTotal(newProducts),
                totalIva: getTotalIva(newProducts),
            }))
        },
        [configs, password, loading, saleInfo, getTotal, getTotalIva]
    )

    const handleChangeProductQnt = (product: ProductInSale, qnt: number) => {
        const isMasterBranch =
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch

        const canShowStock =
            accountInfo?.isMaster ||
            accountInfo?.rules.findIndex(
                (r) =>
                    r.group.toLowerCase() === 'produtos' &&
                    r.name.toLowerCase() === 'listar estoque'
            ) !== -1

        if (isMasterBranch && !saleInfo.branch) {
            snack.error('Selecione una sucursal!')
            return
        }

        let stock = getProductStockInBranch(
            product.product,
            saleInfo.branch,
            isMasterBranch
        )

        if (saleInfo.prevEditingProducts && saleInfo.isEditing) {
            const foundPrevProduct = saleInfo.prevEditingProducts.find(
                (p) => p.product.id === product.product.id
            )
            if (foundPrevProduct) {
                stock = currency(stock).add(foundPrevProduct.qnt).value
            }
        }

        if (!product.product.sellWithoutStock && stock < qnt) {
            if (canShowStock) snack.error('Estoque máximo do produto: ' + stock)
            else snack.error('O produto está sem estoque')
            return
        }

        const newProducts = [...saleInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        const { value, notePrice, iva, proporcaoIva } = getProductPrice(
            product.product,
            saleInfo.coin!,
            qnt,
            newProduct.discount,
            saleInfo.client,
            product.product.autoPrice ? newProduct.price : undefined,
            newProduct.selectedNamedPrice
        )

        newProduct.qnt = qnt
        newProduct.price = value
        newProduct.notePrice = notePrice
        newProduct.subtotal = newProduct.qnt * newProduct.notePrice
        newProduct.ivaValue = iva
        newProduct.proporcaoIva = proporcaoIva

        setSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductPrice = async (
        product: ProductInSale,
        price: number
    ) => {
        const isMasterBranch =
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch
        if (isMasterBranch && !saleInfo.branch) {
            snack.error('Selecione una sucursal!')
            return
        }

        const newProducts = [...saleInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        const { value, notePrice, iva, proporcaoIva } = getProductPrice(
            product.product,
            saleInfo.coin!,
            newProduct.qnt,
            newProduct.discount,
            saleInfo.client,
            price,
            newProduct.selectedNamedPrice
        )

        newProduct.price = value
        newProduct.notePrice = notePrice
        newProduct.subtotal = newProduct.qnt * newProduct.notePrice
        newProduct.ivaValue = iva
        newProduct.proporcaoIva = proporcaoIva

        setSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductTmpPrice = async (
        product: ProductInSale,
        price: number
    ) => {
        const isMasterBranch =
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch
        if (isMasterBranch && !saleInfo.branch) {
            snack.error('Selecione una sucursal!')
            return
        }

        const newProducts = [...saleInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        newProduct.tmpPrice = price

        setSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
        }))
    }

    const handleChangeProductDiscount = (
        products: ProductInSale[],
        discount: number
    ) => {
        const newProducts = [...saleInfo.products]

        for (const product of products) {
            const newProduct = newProducts.find((p) => p.id === product.id)
            if (!newProduct) continue

            const { value, notePrice, iva, proporcaoIva } = getProductPrice(
                product.product,
                saleInfo.coin!,
                newProduct.qnt,
                discount,
                saleInfo.client,
                product.product.autoPrice ? newProduct.price : undefined,
                newProduct.selectedNamedPrice
            )

            newProduct.discount = discount
            newProduct.price = value
            newProduct.notePrice = notePrice
            newProduct.subtotal = newProduct.qnt * newProduct.notePrice
            newProduct.ivaValue = iva
            newProduct.proporcaoIva = proporcaoIva
        }

        setSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductSelectedNamedPrice = (
        product: ProductInSale,
        selectedNamedPrice: string,
        price: number
    ) => {
        const newProducts = [...saleInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        const { value, notePrice, iva, proporcaoIva } = getProductPrice(
            product.product,
            saleInfo.coin!,
            newProduct.qnt,
            newProduct.discount,
            saleInfo.client,
            product.product.autoPrice ? price : undefined,
            selectedNamedPrice
        )

        newProduct.selectedNamedPrice = selectedNamedPrice
        newProduct.price = value
        newProduct.notePrice = notePrice
        newProduct.subtotal = newProduct.qnt * newProduct.notePrice
        newProduct.ivaValue = iva
        newProduct.proporcaoIva = proporcaoIva

        setSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeClient = (client: ClientInfo | null) => {
        if (client && !client.isActive) {
            snack.error('O cliente não está ativo!')
            return
        }

        const newProducts = [...saleInfo.products]

        for (const product of newProducts) {
            const changeToClientPriceForAutoPrice =
                product.product.autoPrice &&
                !!client &&
                !!client.prefferedPrice &&
                product.product.namedPrice.some(
                    (np) => np.name === client.prefferedPrice?.toString()
                )

            if (changeToClientPriceForAutoPrice) product.discount = 0

            const { value, notePrice, iva, proporcaoIva, selectedNamedPrice } =
                getProductPrice(
                    product.product,
                    saleInfo.coin!,
                    product.qnt,
                    product.discount,
                    client,
                    product.product.autoPrice &&
                        !changeToClientPriceForAutoPrice
                        ? product.price
                        : undefined,
                    product.selectedNamedPrice
                )

            product.selectedNamedPrice = selectedNamedPrice ?? undefined
            product.price = value
            product.notePrice = notePrice
            product.subtotal = product.qnt * product.notePrice
            product.ivaValue = iva
            product.proporcaoIva = proporcaoIva
        }

        setSaleInfo((prev) => ({
            ...prev,
            client: client,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeCoin = (coin: CoinInfo) => {
        const newProducts = [...saleInfo.products]

        for (const product of newProducts) {
            let price = product.price

            if (saleInfo.coin?.id && saleInfo.coin?.id !== coin.id) {
                const exchange =
                    coin.exchanges.find(
                        (e) => e.coin.id === saleInfo.coin?.id
                    ) ?? null
                price = getExchangeValue({
                    amount: product.price,
                    exchange: exchange?.saleExchange ?? 0,
                    coinExchangeType: exchange?.flagReverseExchange ?? 0,
                    order: 'reverse',
                })
            }

            const { value, notePrice, iva, proporcaoIva, buyPrice, unitPrice } =
                getProductPrice(
                    product.product,
                    coin,
                    product.qnt,
                    product.discount,
                    saleInfo.client,
                    product.product.autoPrice ? price : undefined,
                    product.selectedNamedPrice
                )

            product.price = value
            product.notePrice = notePrice
            product.subtotal = product.qnt * product.notePrice
            product.ivaValue = iva
            product.proporcaoIva = proporcaoIva
            product.buyPrice = buyPrice
            product.unitPrice = unitPrice
        }

        setSaleInfo((prev) => ({
            ...prev,
            coin: coin,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeObservation = (o: string) => {
        setSaleInfo((prev) => ({
            ...prev,
            observation: o,
        }))
    }

    const handleChangePayment = (
        paymentType: number,
        payment: PaymentInSale
    ) => {
        setSaleInfo((prev) => ({
            ...prev,
            paymentType: paymentType,
            payments: [payment],
        }))
    }

    const handleAddNewProductsInPreSale = (products: ProductInfo[]) => {
        const isMasterBranch =
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch
        if (isMasterBranch && !preSaleInfo.branch) {
            snack.error('Seleccione una sucursal!')
            return
        }

        const newProducts = [...preSaleInfo.products]
        let count = preSaleInfo.productCount ?? 0

        products.forEach((product) => {
            const weight = product.weight ?? 1

            const productInPreSale = preSaleInfo.products.find(
                (p) => p.product.id === product.id
            )
            if (productInPreSale && productInPreSale.product.autoSum) {
                handleChangeProductQntInPreSale(
                    productInPreSale,
                    currency(productInPreSale.qnt).add(weight).value
                )
                return
            }
            if (newProducts.length === configs.productLimitInSale) {
                snack.error(
                    `¡Se ha excedido el límite de productos para la venta! (${
                        configs.productLimitInSale
                    } iten${configs.productLimitInSale !== 1 ? 's' : ''}.)`
                )
                return
            }
            if (!product.isAtive) {
                snack.error('O produto está desativado!')
                return
            }

            let price = product.salePrice

            if (product.coin?.id && product.coin?.id !== preSaleInfo.coin?.id) {
                const exchange =
                    preSaleInfo.coin?.exchanges.find(
                        (e) => e.coin.id === product.coin?.id
                    ) ?? null
                price = getExchangeValue({
                    amount: price,
                    exchange: exchange?.saleExchange ?? 0,
                    coinExchangeType: exchange?.flagReverseExchange ?? 0,
                    order: 'reverse',
                })
            }

            const { value, notePrice, iva, proporcaoIva, selectedNamedPrice } =
                getProductPrice(
                    product,
                    preSaleInfo.coin!,
                    weight,
                    0,
                    preSaleInfo.client
                    /* product.autoPrice ? price : undefined */
                )

            count += 1
            newProducts.push({
                product: product,
                qnt: weight,
                id: count,
                subtotal: notePrice * weight,
                price: value,
                notePrice: notePrice,
                discount: 0,
                ivaValue: iva,
                proporcaoIva: proporcaoIva,
                selectedNamedPrice: selectedNamedPrice ?? undefined,
            })
        })

        setPreSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
            productCount: count,
        }))
    }

    const handleRemoveProductInPreSale = (product: ProductInPreSale) => {
        const newProducts = [...preSaleInfo.products]
        const index = newProducts.findIndex((p) => p.id === product.id)
        if (index === -1) return

        newProducts.splice(index, 1)

        setPreSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductQntInPreSale = (
        product: ProductInPreSale,
        qnt: number
    ) => {
        const newProducts = [...preSaleInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        const { value, notePrice, iva, proporcaoIva } = getProductPrice(
            product.product,
            preSaleInfo.coin!,
            qnt,
            newProduct.discount,
            preSaleInfo.client,
            product.product.autoPrice ? newProduct.price : undefined,
            newProduct.selectedNamedPrice
        )

        newProduct.qnt = qnt
        newProduct.price = value
        newProduct.notePrice = notePrice
        newProduct.subtotal = newProduct.qnt * newProduct.notePrice
        newProduct.ivaValue = iva
        newProduct.proporcaoIva = proporcaoIva

        setPreSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductPriceInPreSale = (
        product: ProductInPreSale,
        price: number
    ) => {
        const newProducts = [...preSaleInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        const { value, notePrice, iva, proporcaoIva } = getProductPrice(
            product.product,
            preSaleInfo.coin!,
            newProduct.qnt,
            newProduct.discount,
            preSaleInfo.client,
            price,
            newProduct.selectedNamedPrice
        )

        newProduct.price = value
        newProduct.notePrice = notePrice
        newProduct.subtotal = newProduct.qnt * newProduct.notePrice
        newProduct.ivaValue = iva
        newProduct.proporcaoIva = proporcaoIva

        setPreSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductTmpPriceInPreSale = async (
        product: ProductInPreSale,
        price: number
    ) => {
        const newProducts = [...preSaleInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        newProduct.tmpPrice = price

        setPreSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
        }))
    }

    const handleChangeProductDiscountInPreSale = (
        product: ProductInPreSale,
        discount: number
    ) => {
        const newProducts = [...preSaleInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        const { value, notePrice, iva, proporcaoIva } = getProductPrice(
            product.product,
            preSaleInfo.coin!,
            newProduct.qnt,
            discount,
            preSaleInfo.client,
            product.product.autoPrice ? newProduct.price : undefined,
            newProduct.selectedNamedPrice
        )

        newProduct.discount = discount
        newProduct.price = value
        newProduct.notePrice = notePrice
        newProduct.subtotal = newProduct.qnt * newProduct.notePrice
        newProduct.ivaValue = iva
        newProduct.proporcaoIva = proporcaoIva

        setPreSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeProductSelectedNamedPriceInPreSale = (
        product: ProductInPreSale,
        selectedNamedPrice: string,
        price: number
    ) => {
        const newProducts = [...preSaleInfo.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        const { value, notePrice, iva, proporcaoIva } = getProductPrice(
            product.product,
            preSaleInfo.coin!,
            newProduct.qnt,
            newProduct.discount,
            preSaleInfo.client,
            product.product.autoPrice ? price : undefined,
            selectedNamedPrice
        )

        newProduct.selectedNamedPrice = selectedNamedPrice
        newProduct.price = value
        newProduct.notePrice = notePrice
        newProduct.subtotal = newProduct.qnt * newProduct.notePrice
        newProduct.ivaValue = iva
        newProduct.proporcaoIva = proporcaoIva

        setPreSaleInfo((prev) => ({
            ...prev,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeClientInPreSale = (client: ClientInfo | null) => {
        if (client && !client.isActive) {
            snack.error('O cliente não está ativo!')
            return
        }

        const newProducts = [...preSaleInfo.products]

        for (const product of newProducts) {
            const changeToClientPriceForAutoPrice =
                product.product.autoPrice &&
                !!client &&
                !!client.prefferedPrice &&
                product.product.namedPrice.some(
                    (np) => np.name === client.prefferedPrice?.toString()
                )

            if (changeToClientPriceForAutoPrice) product.discount = 0

            const { value, notePrice, iva, proporcaoIva, selectedNamedPrice } =
                getProductPrice(
                    product.product,
                    preSaleInfo.coin!,
                    product.qnt,
                    product.discount,
                    client,
                    product.product.autoPrice &&
                        !changeToClientPriceForAutoPrice
                        ? product.price
                        : undefined,
                    product.selectedNamedPrice
                )

            product.selectedNamedPrice = selectedNamedPrice ?? undefined
            product.price = value
            product.notePrice = notePrice
            product.subtotal = product.qnt * product.notePrice
            product.ivaValue = iva
            product.proporcaoIva = proporcaoIva
        }

        setPreSaleInfo((prev) => ({
            ...prev,
            client: client,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleChangeCoinInPreSale = (coin: CoinInfo) => {
        const newProducts = [...preSaleInfo.products]

        for (const product of newProducts) {
            let price = product.price

            if (preSaleInfo.coin?.id && preSaleInfo.coin?.id !== coin.id) {
                const exchange =
                    coin.exchanges.find(
                        (e) => e.coin.id === preSaleInfo.coin?.id
                    ) ?? null
                price = getExchangeValue({
                    amount: product.price,
                    exchange: exchange?.saleExchange ?? 0,
                    coinExchangeType: exchange?.flagReverseExchange ?? 0,
                    order: 'reverse',
                })
            }

            const { value, notePrice, iva, proporcaoIva } = getProductPrice(
                product.product,
                coin,
                product.qnt,
                product.discount,
                preSaleInfo.client,
                product.product.autoPrice ? price : undefined,
                product.selectedNamedPrice
            )

            product.price = value
            product.notePrice = notePrice
            product.subtotal = product.qnt * product.notePrice
            product.ivaValue = iva
            product.proporcaoIva = proporcaoIva
        }

        setPreSaleInfo((prev) => ({
            ...prev,
            coin: coin,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const handleLoadExcelInPreSale = async (base64: string) => {
        const isMasterBranch =
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch
        if (isMasterBranch && !preSaleInfo.branch) {
            snack.error('Seleccione una sucursal!')
            return
        }

        loading.show()
        try {
            const { products, notFoundProducts } = await loadProductsFromExcel(
                base64,
                coins,
                preSaleInfo.branch?.id
            )

            const newProducts = [...preSaleInfo.products]
            let count = preSaleInfo.productCount ?? 0

            products.forEach((product) => {
                const weight = product.qnt ?? 1

                const productInPreSale = preSaleInfo.products.find(
                    (p) => p.product.id === product.product.id
                )
                if (productInPreSale && productInPreSale.product.autoSum) {
                    handleChangeProductQntInPreSale(
                        productInPreSale,
                        currency(productInPreSale.qnt).add(weight).value
                    )
                    return
                }
                if (newProducts.length === configs.productLimitInSale) {
                    snack.error(
                        `¡Se ha excedido el límite de productos para la venta! (${
                            configs.productLimitInSale
                        } iten${configs.productLimitInSale !== 1 ? 's' : ''}.)`
                    )
                    return
                }
                if (!product.product.isAtive) {
                    snack.error('O produto está desativado!')
                    return
                }

                let price = product.price

                if (
                    product.product.coin?.id &&
                    product.product.coin?.id !== preSaleInfo.coin?.id
                ) {
                    const exchange =
                        preSaleInfo.coin?.exchanges.find(
                            (e) => e.coin.id === product.product.coin?.id
                        ) ?? null
                    price = getExchangeValue({
                        amount: price,
                        exchange: exchange?.saleExchange ?? 0,
                        coinExchangeType: exchange?.flagReverseExchange ?? 0,
                        order: 'reverse',
                    })
                }

                const {
                    value,
                    notePrice,
                    iva,
                    proporcaoIva,
                    selectedNamedPrice,
                } = getProductPrice(
                    product.product,
                    preSaleInfo.coin!,
                    weight,
                    0,
                    preSaleInfo.client,
                    product.product.autoPrice ? price : undefined
                )

                count += 1
                newProducts.push({
                    product: product.product,
                    qnt: weight,
                    id: count,
                    subtotal: notePrice * weight,
                    price: value,
                    notePrice: notePrice,
                    discount: 0,
                    ivaValue: iva,
                    proporcaoIva: proporcaoIva,
                    selectedNamedPrice: selectedNamedPrice ?? undefined,
                })
            })

            setPreSaleInfo((prev) => ({
                ...prev,
                products: newProducts,
                total: getTotal(newProducts),
                totalIva: getTotalIva(newProducts),
                productCount: count,
            }))
            if (notFoundProducts.length)
                setNotFoundProductsInExcel(notFoundProducts)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }

    const handleLoadFile = (coin: CoinInfo) => {
        const newProducts = [...preSaleInfo.products]

        for (const product of newProducts) {
            let price = product.price

            if (preSaleInfo.coin?.id && preSaleInfo.coin?.id !== coin.id) {
                const exchange =
                    coin.exchanges.find(
                        (e) => e.coin.id === preSaleInfo.coin?.id
                    ) ?? null
                price = getExchangeValue({
                    amount: product.price,
                    exchange: exchange?.saleExchange ?? 0,
                    coinExchangeType: exchange?.flagReverseExchange ?? 0,
                    order: 'reverse',
                })
            }

            const { value, notePrice, iva, proporcaoIva } = getProductPrice(
                product.product,
                coin,
                product.qnt,
                product.discount,
                preSaleInfo.client,
                product.product.autoPrice ? price : undefined,
                product.selectedNamedPrice
            )

            product.price = value
            product.notePrice = notePrice
            product.subtotal = product.qnt * product.notePrice
            product.ivaValue = iva
            product.proporcaoIva = proporcaoIva
        }

        setPreSaleInfo((prev) => ({
            ...prev,
            coin: coin,
            products: newProducts,
            total: getTotal(newProducts),
            totalIva: getTotalIva(newProducts),
        }))
    }

    const validate = (): boolean => {
        const usingEletronicNote = saleInfo.branch
            ? !!saleInfo.branch.useElectronicNote
            : !!configs.useElectronicNote

        const canSellLowerThanPurchasePrice =
            accountInfo?.isMaster ||
            !!accountInfo?.permissions?.includes(
                'Vendas:Vender abaixo do preço de compra'
            )

        if (!saleInfo.client) {
            snack.error('Selecione um cliente')
            return false
        }
        if (saleInfo.client.id && usingEletronicNote) {
            const isClientOk = validateClient(saleInfo.client)
            if (!isClientOk) {
                setClientInfo({
                    ...saleInfo.client,
                    step: 1,
                    isEditing: true,
                })
                setOpenRegisterClientModal(true)
                snack.error(
                    'El cliente tiene datos no válidos, corríjalos antes de continuar'
                )
                return false
            }
        }

        if (!saleInfo.products.length) {
            snack.error('Selecione um produto!')
            return false
        }
        for (const product of saleInfo.products) {
            if (!product.price) {
                snack.error(
                    `O producto ${product.product.name} está sin precio`
                )
                return false
            }
            if (!product.qnt) {
                snack.error(
                    `O producto ${product.product.name} está sin cantidad`
                )
                return false
            }
            if (
                product.buyPrice &&
                product.product.autoPrice &&
                product.price < product.buyPrice &&
                !canSellLowerThanPurchasePrice
            ) {
                snack.error(
                    `O producto ${
                        product.product.name
                    } tiene un precio inferior al precio de compra (${saleInfo
                        .coin?.symbol} ${maskCurrency(
                        product.buyPrice,
                        saleInfo.coin
                    )})`
                )
                return false
            }
            if (
                product.unitPrice &&
                product.product.autoPrice &&
                product.price < product.unitPrice &&
                configs.blockSellLowerThanSalePriceAutoprice
            ) {
                snack.error(
                    `O producto ${
                        product.product.name
                    } tiene un precio inferior al precio de venta (${saleInfo
                        .coin?.symbol} ${maskCurrency(
                        product.unitPrice,
                        saleInfo.coin
                    )})`
                )
                return false
            }
        }
        if (saleInfo.products.filter((p) => !p.price || !p.qnt).length) {
            snack.error('A quantidade e o preço não podem ser 0!')
            return false
        }
        if (
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch &&
            !saleInfo.branch
        ) {
            snack.error('Selecione una sucursal!')
            return false
        }
        return true
    }

    const validatePreSale = (): boolean => {
        /* if (!preSaleInfo.title) {
            snack.error('Selecione um título')
            return false
        } */
        if (!preSaleInfo.products.length) {
            snack.error('Selecione um produto!')
            return false
        }
        if (preSaleInfo.products.filter((p) => !p.qnt).length) {
            snack.error('A quantidade não pode ser 0!')
            return false
        }
        if (
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch &&
            !preSaleInfo.branch
        ) {
            snack.error('Selecione una sucursal!')
            return false
        }
        return true
    }

    const handleCreateNewSale = async ({
        initialFees,
        parcels,
        payments,
        paymentType,
        useLegalNote,
        discount,
        changes,
        employeeId,
        note,
        noteCoin,
        transactionType,
        isPix,
        isParaguayTransfer,
        creditDiscount,
    }: HandleCreateNewSaleInterface) => {
        let sale: SaleInfo | null = null
        let pix: Pix | null = null
        let paraguayTransfer: ParaguayTransfer | null = null
        loading.show()
        try {
            if (!saleInfo.isEditing) {
                const res = await createSale(
                    {
                        flag_forma_pagamento: paymentType ?? null,
                        id_cliente: saleInfo.client?.id
                            ? saleInfo.client.id
                            : undefined,
                        nota_entrega: saleInfo.observation
                            ? saleInfo.observation
                            : null,
                        sale_product: saleInfo.products.map((p) => ({
                            quantidade_produto: p.qnt,
                            id_produto: p.product.id ?? 0,
                            desconto: p.discount / 100,
                            nome_preco: p.selectedNamedPrice,
                            preco: p.product.autoPrice ? p.price : undefined,
                            id_produto_pre_venda: p.preSaleProductId,
                        })),
                        sale_payment: payments?.map((payment) => ({
                            id_metodo_pagamento:
                                payment.selectedPaymentMethod?.id ?? 0,
                            id_moeda: payment.selectedCoin?.id ?? 0,
                            valor_pagamento: payment.exchangeAmountPayed,
                            comprovante_imagem: payment.proofImage,
                            comprovante_texto: payment.proofText,
                            id_funcionario: null,
                        })),
                        desconto: discount ?? null,
                        desconto_credito: creditDiscount ?? null,
                        trocos: changes?.map((c) => ({
                            id_moeda: c.coin.id as number,
                            valor: c.value,
                        })),
                        use_legal_note: useLegalNote,
                        id_nota: note?.id,
                        id_moeda_fatura: noteCoin?.id,
                        tipo_transacao_fatura: transactionType ?? 1,
                        id_filial_master: saleInfo.branch?.id,
                        id_pre_venda: saleInfo.preSale?.id,
                        juros_inicial:
                            paymentType === PaymentType.Parcel && initialFees
                                ? initialFees / 100
                                : undefined,
                        parcelas:
                            paymentType === PaymentType.Parcel && parcels
                                ? parcels
                                : undefined,
                        id_funcionario: saleInfo.employee?.id,
                        comissao:
                            typeof saleInfo.comission === 'number'
                                ? saleInfo.comission
                                : undefined,
                        id_guia: saleInfo.guide?.id,
                        porcentagem_guia:
                            typeof saleInfo.guidePercentage === 'number'
                                ? saleInfo.guidePercentage
                                : undefined,
                        id_funcionario_pagamento: employeeId,
                        isPix: isPix,
                        isParaguayTransfer: isParaguayTransfer,
                        id_moeda: saleInfo.coin?.id ?? 0,
                    },
                    coins
                )
                sale = res.sale
                pix = res.pix
                paraguayTransfer = res.paraguayTransfer
            } else {
                await updateSale({
                    id_venda: saleInfo.id as number,
                    sale_product: saleInfo.products.map((p) => ({
                        quantidade_produto: p.qnt,
                        id_produto: p.product.id ?? 0,
                        desconto: p.discount / 100,
                        nome_preco: p.selectedNamedPrice,
                        preco: p.product.autoPrice ? p.price : undefined,
                        id_produto_pre_venda: p.preSaleProductId,
                    })),
                    nota_entrega: saleInfo.observation
                        ? saleInfo.observation
                        : null,
                })
            }

            snack.success(
                `Venda ${
                    !saleInfo.isEditing ? 'criada' : 'atualizada'
                } com sucesso!`,
                2000
            )
            /* navigate('/system/sale') */
            setSaleInfo({ ...saleContextDefault })
        } catch (error) {
            if (error instanceof CashierClosedSaleError)
                snack.error('O caixa está fechado!')
            else snack.connectionFail(error)
        }
        loading.hide()

        return {
            sale,
            pix,
            paraguayTransfer,
        }
    }

    const handleCreateNewPreSale = async () => {
        loading.show()
        try {
            let preSale: PreSaleInfo | null = null
            const body: CreatePreSaleInterface = {
                titulo: preSaleInfo.title,
                produtos: preSaleInfo.products.map((p) => ({
                    id_produto: p.product.id ?? 0,
                    quantidade_produto: p.qnt,
                    preco_selecionado: p.selectedNamedPrice,
                    desconto: p.discount / 100,
                    preco: p.product.autoPrice ? p.price : undefined,
                })),
                flag_ativo: 1,
                anotacao: preSaleInfo.annotation,
                id_cliente: preSaleInfo.client?.id
                    ? preSaleInfo.client.id
                    : null,
                id_filial_master: preSaleInfo.branch?.id,
                id_funcionario: preSaleInfo.employee?.id ?? null,
                id_moeda: preSaleInfo.coin?.id ?? 0,
                flag_reservar_estoque: Number(preSaleInfo.reservedStock),
            }
            if (!preSaleInfo.isEditing)
                preSale = await createPreSale(body, coins)
            else
                preSale = await updatePreSale(
                    {
                        ...body,
                        id_pre_venda: preSaleInfo.id as number,
                    },
                    coins
                )
            snack.success(
                `Pré-venda ${
                    !preSaleInfo.isEditing ? 'criada' : 'atualizada'
                } com sucesso!`,
                2000
            )
            navigate('/system/sale/budget', {
                state: {
                    preSaleToPrint: preSale,
                },
            })
            setPreSaleInfo({ ...preSaleContextDefault })
        } catch (error) {
            if (error instanceof CashierClosedSaleError)
                snack.error('O caixa está fechado!')
            else snack.connectionFail(error)
        }
        loading.hide()
    }

    const handleChangeSelectedProducts = (products: ProductInSale[]) => {
        setSaleInfo((prev) => ({
            ...prev,
            selectedProducts: products,
        }))
    }

    const startNewSale = () => {
        setSaleInfo({ ...saleContextDefault })
    }

    const duplicateSale = (sale: SaleInfo) => {
        const products = sale.products.map((p, i) => {
            const product = { ...p }
            product.discount = (p.discount ?? 0) * 100
            const { value, notePrice, iva, proporcaoIva, buyPrice, unitPrice } =
                getProductPrice(
                    product.product,
                    sale.coin!,
                    product.qnt,
                    product.discount,
                    sale.client,
                    product.product.autoPrice ? product.price : undefined,
                    product.selectedNamedPrice
                )

            product.price = value
            product.notePrice = notePrice
            product.subtotal = product.notePrice * product.qnt
            product.id = i + 1
            product.ivaValue = iva
            product.proporcaoIva = proporcaoIva
            product.buyPrice = buyPrice
            product.unitPrice = unitPrice

            return product
        })

        const newSale: SaleInfo = {
            ...saleContextDefault,
            products,
            client: sale.client,
            coin: sale.coin,
            paymentType: sale.paymentType,
            paymentTypeShow: sale.paymentTypeShow,
            observation: sale.observation,
            branch: sale.branch,
            employee: sale.employee,
            guide: sale.guide,
            guidePercentage: sale.guidePercentage,
            total: getTotal(products),
            totalIva: getTotalIva(products),
            productCount: products.length,
            isEditing: false,
        }

        setSaleInfo(newSale)
        localStorage.setItem('saleInfo', JSON.stringify(newSale))
    }

    const startNewPreSale = () => {
        setPreSaleInfo({ ...preSaleContextDefault })
    }

    const editSale = async (id: number) => {
        try {
            const sale = await getSaleById(id, coins)

            sale.products.forEach((p, i) => {
                p.discount *= 100
                const {
                    value,
                    notePrice,
                    iva,
                    proporcaoIva,
                    buyPrice,
                    unitPrice,
                } = getProductPrice(
                    p.product,
                    sale.coin!,
                    p.qnt,
                    p.discount,
                    sale.client,
                    p.product.autoPrice ? p.price : undefined,
                    p.selectedNamedPrice
                )

                p.price = value
                p.notePrice = notePrice
                p.subtotal = p.notePrice * p.qnt
                p.id = i + 1
                p.ivaValue = iva
                p.proporcaoIva = proporcaoIva
                p.buyPrice = buyPrice
                p.unitPrice = unitPrice
            })

            setSaleInfo(() => ({
                ...sale,
                total: getTotal(sale.products),
                totalIva: getTotalIva(sale.products),
                isEditing: true,
                id,
                prevEditingProducts: JSON.parse(JSON.stringify(sale.products)),
                productCount: sale.products.length,
            }))
        } catch (error) {
            console.log(error)
            snack.error('Venda não encontrada')
            throw new Error('Sale not found')
        }
    }

    const editPreSale = async (id: number) => {
        try {
            const preSale = await getPreSaleById(id, coins)

            preSale.products.forEach((p, i) => {
                const { value, notePrice, iva, proporcaoIva } = getProductPrice(
                    p.product,
                    preSale.coin!,
                    p.qnt,
                    p.discount,
                    preSale.client,
                    p.product.autoPrice ? p.price : undefined,
                    p.selectedNamedPrice
                )

                p.price = value
                p.notePrice = notePrice
                p.subtotal = p.notePrice * p.qnt
                p.id = i + 1
                p.ivaValue = iva
                p.proporcaoIva = proporcaoIva
            })

            setPreSaleInfo(() => ({
                ...preSale,
                total: getTotal(preSale.products),
                totalIva: getTotalIva(preSale.products),
                isEditing: true,
                productCount: preSale.products.length,
            }))
        } catch (error) {
            console.log(error)
            snack.error('Orçamento não encontrado')
            throw new Error('Budget not found')
        }
    }

    return (
        <saleContext.Provider
            value={{
                saleInfo,
                setSaleInfo,
                preSaleInfo,
                setPreSaleInfo,
                validatePreSale,
                handleCreateNewPreSale,
                handleChangeBill,
                handleAddNewProducts,
                handleLoadPreSale,
                handleRemoveProduct,
                handleChangeProductQnt,
                handleChangeProductPrice,
                handleChangeProductTmpPrice,
                handleChangeProductDiscount,
                handleChangeProductSelectedNamedPrice,
                handleChangeClient,
                handleChangeCoin,
                handleChangePayment,
                handleAddNewProductsInPreSale,
                handleRemoveProductInPreSale,
                handleChangeProductQntInPreSale,
                handleChangeProductPriceInPreSale,
                handleChangeProductTmpPriceInPreSale,
                handleChangeProductDiscountInPreSale,
                handleChangeProductSelectedNamedPriceInPreSale,
                handleChangeClientInPreSale,
                handleChangeCoinInPreSale,
                handleLoadExcelInPreSale,
                validate,
                handleCreateNewSale,
                handleChangeObservation,
                handleChangeSelectedProducts,
                startNewSale,
                startNewPreSale,
                duplicateSale,
                editSale,
                editPreSale,
                paymentMethod,
                setPaymentMethod,
            }}
        >
            {!!notFoundProductsInExcel && (
                <GenericModal
                    open
                    title="Atención"
                    textButton="Confirmar"
                    handleSubmit={() => setNotFoundProductsInExcel(null)}
                    handleClose={() => setNotFoundProductsInExcel(null)}
                >
                    <Typography>
                        {t('No se encontraron los siguientes productos: ')}
                    </Typography>
                    <List>
                        {notFoundProductsInExcel.map(
                            (notFoundProductInExcel) => (
                                <ListItem>
                                    <ListItemText>
                                        {notFoundProductInExcel.code} -{' '}
                                        {notFoundProductInExcel.name} -{' '}
                                        {notFoundProductInExcel.qnt} -{' '}
                                        {notFoundProductInExcel.price}
                                    </ListItemText>
                                </ListItem>
                            )
                        )}
                    </List>
                </GenericModal>
            )}
            {children}
        </saleContext.Provider>
    )
}
